import './Facets.scss';

import classnames from 'classnames';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	facetSelected,
	getLister,
	updateDismissFiltersUrl,
} from '../../actions/lister-actions';
import TRANSLATIONS from '../../translations/translations@default.json';
import { Breakpoint } from '../../utilities/ui-utilities';
import FacetValue from './FacetValue';
import NewEnergyRatingInformation from './NewEnergyRatingInformation/NewEnergyRatingInformation';

export const FacetType = ({
	amountOfFacetsToInitallyRender,
	facet,
	position,
	largeLayoutScrollToTop,
	storeSelectedFacets,
}) => {
	const { dismissFiltersUrl, pageTitle, products } = useSelector(
		(state) => state
	);

	const facetsToInitallyRender = useRef(
		amountOfFacetsToInitallyRender > 0 ? amountOfFacetsToInitallyRender : 9
	);
	const [hidden, setHidden] = useState(false);
	const [showInitiallyHidden, setShowInitiallyHidden] = useState(false);

	const dispatch = useDispatch();

	const largeLayoutResetFacet = (resetUrl) => {
		dispatch(
			getLister(resetUrl, {
				type: '',
				trackHistory: true,
			})
		);
		dispatch(updateDismissFiltersUrl(resetUrl));
	};

	const resetRadioButton = () => {
		const selectedRadioButton = facet.FacetValues.filter(
			({ Selected }) => Selected
		)[0];

		const facetData = {
			facetTypeHeader: facet.Name,
			facetTypePosition: position,
			facetValue: selectedRadioButton,
			facetValuePosition: 0,
			pageUrl: dismissFiltersUrl,
			pageTitle: pageTitle,
		};

		dispatch(facetSelected(facetData));
		dispatch(
			getLister(facet.ResetUrl, {
				type: '',
				trackHistory: true,
			})
		);
	};
	const { FacetValues, IsMultiSelect } = facet;
	const isRadioButton = !IsMultiSelect;

	let selectedFacets = FacetValues.filter((f) => f.Selected)
		.map((f) => f.Name)
		.join(', ');

	const numberOfSelectedFacets = (facetsSelected) => {
		const total = facetsSelected.filter(({ Selected }) => Selected);

		return total.length;
	};

	let shouldRender = true;

	if (facet.Id === 'onlyshowinstock') {
		shouldRender = products.some(({ State }) => State === 'LiveProduct');
	}

	if (!shouldRender) {
		return null;
	}

	return (
		<div
			className="facet-type bg-white mb-2 rounded-lg"
			data-facet-name={facet.Name}>
			<div
				className={classnames('facet-type__header flex relative p-4 pr-12', {
					collapsed: hidden,
				})}>
				<div className="flex-grow">
					<h3 className="text-title md:text-sm">{facet.Name}</h3>
					{selectedFacets && hidden && (
						<Breakpoint.BelowSmallOnly>
							<div className="facet-type__selected-filters text-xs">
								{selectedFacets}
							</div>
						</Breakpoint.BelowSmallOnly>
					)}
				</div>

				{isRadioButton && facet.Selected && (
					<React.Fragment>
						<Breakpoint.BelowSmallOnly>
							<a
								href={facet.ResetUrl}
								className="facet-type__reset relative text-body-sm text-cta-action border-none ml-2 p-0"
								onClick={(e) => {
									e.preventDefault();
									resetRadioButton();
								}}
								data-testid="facet-type-reset-radio-button">
								{TRANSLATIONS.controls.filters.drawer.clearSelected}
							</a>
						</Breakpoint.BelowSmallOnly>
					</React.Fragment>
				)}

				<button
					type="button"
					aria-label={facet.Name}
					className="facet-type__show-hide absolute inset-0 bg-transparent flex items-center justify-end w-full py-0 pl-0 pr-2"
					onClick={() => setHidden((isHidden) => !isHidden)}
					data-testid="facet-type-show-hide-button"
					data-tag-type="accordion"
					data-tag-action={hidden ? 'opened' : 'closed'}
					data-tag-name="group-facet"
					data-tag-facet-name={facet.Name}>
					<div className="text-base">
						<i
							className={classnames(
								'ico ico-chevron-up ico-lg text-cta-action',
								{
									'chevron-up--rebrand': !hidden,
									'chevron-down--rebrand': hidden,
								}
							)}
						/>
					</div>
				</button>
			</div>

			{!hidden && facet.Id?.includes('energyrating') && (
				<NewEnergyRatingInformation />
			)}

			{facet.Selected && (
				<Breakpoint.SmallAndAbove>
					<a
						href={facet.ResetUrl}
						className={classnames(
							'facet-type__reset text-link text-body-xs border-none px-4 py-2',
							{
								collapsed: hidden,
							}
						)}
						onClick={(e) => {
							e.preventDefault();
							largeLayoutResetFacet(facet.ResetUrl);
						}}
						data-tag-name="reset-facet"
						data-tag-group={facet.Name}
						data-tag-selected-facets={numberOfSelectedFacets(facet.FacetValues)}
						data-testid="facet-type-reset-button">
						{TRANSLATIONS.controls.filters.drawer.resetSelected}
					</a>
				</Breakpoint.SmallAndAbove>
			)}

			<div
				className={classnames('facet-type__facets', {
					collapsed: hidden,
					'py-2 md:py-0': !hidden,
				})}>
				{/* Fieldset and legend required for accessibility reasons */}
				<fieldset>
					<legend></legend>
					{facet.Id === 'colour' ? (
						<React.Fragment>
							{/* colour swatches */}
							<Breakpoint.BelowSmallOnly>
								{FacetValues.map((facetValue, index) => {
									return (
										(showInitiallyHidden ||
											index < facetsToInitallyRender.current) && (
											<FacetValue
												key={facetValue.FriendlyName}
												facetTypeId={facet.Id}
												facetTypeHeader={facet.Name}
												facetTypePosition={position}
												facetValue={facetValue}
												facetValuePosition={index}
												isMultiSelect={IsMultiSelect}
												storeSelectedFacets={storeSelectedFacets}
											/>
										)
									);
								})}
							</Breakpoint.BelowSmallOnly>
							<Breakpoint.SmallAndAbove>
								<div
									className="flex flex-wrap py-1 px-4"
									data-testid="facet-type__colour-swatches">
									<div className="flex flex-wrap w-full">
										{FacetValues.map((facetValue, index) => {
											return (
												(showInitiallyHidden ||
													index < facetsToInitallyRender.current) && (
													<FacetValue
														key={facetValue.FriendlyName}
														facetTypeId={facet.Id}
														facetTypeHeader={facet.Name}
														facetTypePosition={position}
														facetValue={facetValue}
														facetValuePosition={index}
														isMultiSelect={IsMultiSelect}
														largeLayoutScrollToTop={largeLayoutScrollToTop}
														storeSelectedFacets={storeSelectedFacets}
													/>
												)
											);
										})}
									</div>
								</div>
							</Breakpoint.SmallAndAbove>
						</React.Fragment>
					) : (
						<React.Fragment>
							{/* no colour swatches */}
							{FacetValues.map((facetValue, index) => {
								return (
									(showInitiallyHidden ||
										index < facetsToInitallyRender.current) && (
										<FacetValue
											key={facetValue.FriendlyName}
											facetTypeId={facet.Id}
											facetTypeHeader={facet.Name}
											facetTypePosition={position}
											facetValue={facetValue}
											facetValuePosition={index}
											isMultiSelect={IsMultiSelect}
											storeSelectedFacets={storeSelectedFacets}
											largeLayoutScrollToTop={largeLayoutScrollToTop}
										/>
									)
								);
							})}
						</React.Fragment>
					)}
				</fieldset>
			</div>

			{FacetValues.length > facetsToInitallyRender.current && (
				<button
					type="button"
					className={classnames(
						'text-body-sm md:text-xs text-link bg-transparent',
						'facet-type__facets-show-more',
						'px-4',
						'md:pt-2',
						{
							collapsed: hidden,
							'pb-4 md:pb-2': !hidden,
						}
					)}
					onClick={() =>
						setShowInitiallyHidden((isInitiallyHidden) => !isInitiallyHidden)
					}
					data-tag-name={
						showInitiallyHidden ? 'less-button-clicked' : 'more-button-clicked'
					}
					data-testid="facets-show-more-less">
					<Breakpoint.BelowSmallOnly>
						{showInitiallyHidden
							? TRANSLATIONS.controls.filters.drawer.mobileHideOptions
							: TRANSLATIONS.controls.filters.drawer.mobileShowMoreOptions}
					</Breakpoint.BelowSmallOnly>
					<Breakpoint.SmallAndAbove>
						{showInitiallyHidden
							? TRANSLATIONS.aside.filters.tabletAndAboveHideOptions
							: TRANSLATIONS.aside.filters.tabletAndAboveShowMoreOptions}
					</Breakpoint.SmallAndAbove>
				</button>
			)}
		</div>
	);
};

export default FacetType;
